import React, { useState, useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';

const O = {
  forest:     '#053418',
  accent:     '#00c853',
  accentSoft: '#d6f1d8',
  ink:        '#1a1a1a',
  muted:      '#6b7368',
  line:       '#e6e8df',
  cream50:    '#fcfcfa',
  cream:      '#fafaf7',
  white:      '#ffffff',
};

const SUPPORTED_CURRENCIES = [
  { code: 'GBP', symbol: '£',   name: 'British Pound',    countryCode: 'GB' },
  { code: 'EUR', symbol: '€',   name: 'Euro',              countryCode: 'EU' },
  { code: 'USD', symbol: '$',   name: 'US Dollar',         countryCode: 'US' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham',        countryCode: 'AE' },
  { code: 'SAR', symbol: '﷼',  name: 'Saudi Riyal',       countryCode: 'SA' },
  { code: 'BHD', symbol: 'BD',  name: 'Bahraini Dinar',    countryCode: 'BH' },
  { code: 'INR', symbol: '₹',  name: 'Indian Rupee',      countryCode: 'IN' },
  { code: 'QAR', symbol: 'QR',  name: 'Qatari Riyal',      countryCode: 'QA' },
  { code: 'AUD', symbol: 'A$',  name: 'Australian Dollar', countryCode: 'AU' },
  { code: 'CAD', symbol: 'C$',  name: 'Canadian Dollar',   countryCode: 'CA' },
];

const BILLING_CYCLES = ['monthly', 'yearly', 'one_time'];
const BILLING_CYCLE_LABELS = { monthly: 'Monthly', yearly: 'Yearly', one_time: 'One-time' };

const PRODUCT_TYPES = [
  { value: 'core',   label: 'Core' },
  { value: 'addon',  label: 'Add-on' },
  { value: 'bundle', label: 'Bundle' },
];

const PRICE_TYPES = [
  { value: 'flat',    label: 'Flat fee' },
  { value: 'percent', label: 'Transaction %' },
  { value: 'hybrid',  label: 'Hybrid (flat + %)' },
];

const emptyPricingRow = () => ({
  currency: 'GBP',
  countryCode: 'GB',
  priceType: 'flat',
  amount: '',
  billingCycle: 'monthly',
  percent: '',
  notes: '',
});

const emptyForm = () => ({
  key: '',
  name: '',
  description: '',
  type: 'core',
  includesProducts: [],
  pricing: [emptyPricingRow()],
});

export default function ProductForm({ open, onClose, onSave, product, allProducts, saving }) {
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (product) {
      setForm({
        key: product.key || product.productKey || '',
        name: product.name || product.productName || '',
        description: product.description || product.productDescription || '',
        type: product.type || (product.isBundle ? 'bundle' : 'core'),
        includesProducts: product.includesProducts?.map(i => typeof i === 'string' ? i : i._id) || [],
        pricing: product.pricing?.length ? product.pricing.map(r => ({
          currency: r.currency || 'GBP',
          countryCode: r.countryCode || 'GB',
          priceType: r.amount != null && r.percent != null ? 'hybrid' : r.percent != null ? 'percent' : 'flat',
          amount: r.amount ?? '',
          billingCycle: r.billingCycle || 'monthly',
          percent: r.percent ?? '',
          notes: r.notes || '',
        })) : [emptyPricingRow()],
      });
    } else {
      setForm(emptyForm());
    }
  }, [product, open]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const updatePricing = (i, field, val) => {
    const rows = [...form.pricing];
    rows[i] = { ...rows[i], [field]: val };
    if (field === 'currency') {
      const cur = SUPPORTED_CURRENCIES.find(c => c.code === val);
      if (cur) rows[i].countryCode = cur.countryCode;
    }
    set('pricing', rows);
  };

  const addPricingRow = () => set('pricing', [...form.pricing, emptyPricingRow()]);
  const removePricingRow = (i) => set('pricing', form.pricing.filter((_, idx) => idx !== i));

  const isBundle = form.type === 'bundle';
  const childOptions = (allProducts || []).filter(p => {
    const pKey = p.key || p.productKey;
    return pKey !== form.key && p.type !== 'bundle';
  });

  const handleSubmit = () => {
    const payload = {
      ...(!isEditing && { key: form.key.trim() }),
      name: form.name.trim(),
      description: form.description.trim(),
      type: form.type,
      includesProducts: isBundle ? form.includesProducts : [],
      pricing: form.pricing.map(r => {
        const row = { currency: r.currency, countryCode: r.countryCode };
        if (r.priceType === 'flat' || r.priceType === 'hybrid') {
          row.amount = parseFloat(r.amount) || 0;
          row.billingCycle = r.billingCycle;
        }
        if (r.priceType === 'percent' || r.priceType === 'hybrid') {
          row.percent = parseFloat(r.percent) || 0;
          if (r.notes) row.notes = r.notes;
        }
        return row;
      }),
    };
    onSave(payload);
  };

  const isEditing = !!product;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100vw', sm: 560 }, backgroundColor: O.cream } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, backgroundColor: O.white, borderBottom: `1px solid ${O.line}` }}>
        <Typography sx={{ fontFamily: 'GilroyBold', fontSize: 16, color: O.forest }}>
          {isEditing ? 'Edit Product' : 'New Product'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 3 }}>

        {/* Basic Info */}
        <SectionLabel>Basic Info</SectionLabel>
        <Field label="Product Key" hint="Kebab-case slug, e.g. table-booking (immutable after creation)">
          <TextField
            fullWidth size="small" placeholder="table-booking"
            value={form.key}
            onChange={e => set('key', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            disabled={isEditing}
            sx={inputSx}
          />
        </Field>
        <Field label="Name">
          <TextField fullWidth size="small" placeholder="Table Booking" value={form.name} onChange={e => set('name', e.target.value)} sx={inputSx} />
        </Field>
        <Field label="Description">
          <TextField fullWidth multiline rows={2} size="small" placeholder="What this product gives the venue…" value={form.description} onChange={e => set('description', e.target.value)} sx={inputSx} />
        </Field>

        <Divider sx={{ my: 2.5, borderColor: O.line }} />

        {/* Type */}
        <SectionLabel>Product Type</SectionLabel>
        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
          <InputLabel sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>Type</InputLabel>
          <Select value={form.type} label="Type" onChange={e => set('type', e.target.value)} sx={inputSx}>
            {PRODUCT_TYPES.map(t => (
              <MenuItem key={t.value} value={t.value}>
                <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>{t.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isBundle && (
          <>
            <Field label="Included products" hint="Select child products (bundles cannot include other bundles)">
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={form.includesProducts}
                  onChange={e => set('includesProducts', e.target.value)}
                  displayEmpty
                  renderValue={selected => selected.length === 0 ? (
                    <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13, color: O.muted }}>Select products…</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(id => {
                        const p = (allProducts || []).find(pr => pr._id === id);
                        return <Chip key={id} label={p ? (p.name || p.productName || id) : id} size="small" sx={{ height: 20, fontFamily: 'GilroySemiBold', fontSize: 11, backgroundColor: O.accentSoft, color: O.forest }} />;
                      })}
                    </Box>
                  )}
                  sx={inputSx}
                >
                  {childOptions.map(p => (
                    <MenuItem key={p._id} value={p._id}>
                      <Checkbox size="small" checked={form.includesProducts.includes(p._id)} sx={{ '&.Mui-checked': { color: O.accent } }} />
                      <ListItemText
                        primary={p.name || p.productName}
                        secondary={p.key || p.productKey}
                        primaryTypographyProps={{ fontFamily: 'GilroySemiBold', fontSize: 13 }}
                        secondaryTypographyProps={{ fontFamily: 'GilroyRegular', fontSize: 11 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
          </>
        )}

        <Divider sx={{ my: 2.5, borderColor: O.line }} />

        {/* Pricing */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <SectionLabel noMargin>Pricing by Region</SectionLabel>
          <Button size="small" startIcon={<AddIcon />} onClick={addPricingRow}
            sx={{ fontFamily: 'GilroySemiBold', fontSize: 12, color: O.forest, textTransform: 'none', backgroundColor: O.accentSoft, px: 1.5, borderRadius: '7px', '&:hover': { backgroundColor: '#c3e8cb' } }}>
            Add region
          </Button>
        </Box>

        {form.pricing.map((row, i) => (
          <Box key={i} sx={{ backgroundColor: O.white, border: `1px solid ${O.line}`, borderRadius: '10px', p: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 12, color: O.muted }}>Region {i + 1}</Typography>
              {form.pricing.length > 1 && (
                <IconButton size="small" onClick={() => removePricingRow(i)} sx={{ color: '#b91c1c' }}><DeleteOutlineIcon fontSize="small" /></IconButton>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5 }}>Currency</InputLabel>
                <Select value={row.currency} label="Currency" onChange={e => updatePricing(i, 'currency', e.target.value)} sx={inputSx}>
                  {SUPPORTED_CURRENCIES.map(c => (
                    <MenuItem key={c.code} value={c.code}>
                      <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 13 }}>{c.code}</Typography>
                      <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 11, color: O.muted, ml: 0.75 }}>{c.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5 }}>Price type</InputLabel>
                <Select value={row.priceType} label="Price type" onChange={e => updatePricing(i, 'priceType', e.target.value)} sx={inputSx}>
                  {PRICE_TYPES.map(t => <MenuItem key={t.value} value={t.value}><Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>{t.label}</Typography></MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {(row.priceType === 'flat' || row.priceType === 'hybrid') && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                <TextField label="Amount" size="small" type="number" inputProps={{ min: 0, step: '0.01' }} value={row.amount} onChange={e => updatePricing(i, 'amount', e.target.value)} sx={inputSx} fullWidth />
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontFamily: 'GilroyRegular', fontSize: 12.5 }}>Billing cycle</InputLabel>
                  <Select value={row.billingCycle} label="Billing cycle" onChange={e => updatePricing(i, 'billingCycle', e.target.value)} sx={inputSx}>
                    {BILLING_CYCLES.map(c => <MenuItem key={c} value={c}><Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>{BILLING_CYCLE_LABELS[c]}</Typography></MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}

            {(row.priceType === 'percent' || row.priceType === 'hybrid') && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                <TextField label="Percent (%)" size="small" type="number" inputProps={{ min: 0, max: 100, step: '0.01' }} value={row.percent} onChange={e => updatePricing(i, 'percent', e.target.value)} sx={inputSx} fullWidth />
                <TextField label="Notes (optional)" size="small" value={row.notes} onChange={e => updatePricing(i, 'notes', e.target.value)} sx={inputSx} fullWidth />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2.5, borderTop: `1px solid ${O.line}`, backgroundColor: O.white, display: 'flex', gap: 1.5 }}>
        <Button fullWidth variant="outlined" onClick={onClose} disabled={saving}
          sx={{ fontFamily: 'GilroySemiBold', fontSize: 14, textTransform: 'none', borderRadius: '10px', borderColor: O.line, color: O.ink, '&:hover': { borderColor: O.muted, backgroundColor: O.cream } }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" onClick={handleSubmit} disabled={saving || !form.key || !form.name}
          disableElevation
          sx={{ fontFamily: 'GilroyBold', fontSize: 14, textTransform: 'none', borderRadius: '10px', backgroundColor: O.accent, color: O.forest, '&:hover': { backgroundColor: '#00a847' }, '&.Mui-disabled': { backgroundColor: O.accentSoft, color: O.muted } }}>
          {saving ? <CircularProgress size={18} sx={{ color: O.forest }} /> : isEditing ? 'Save changes' : 'Create product'}
        </Button>
      </Box>
    </Drawer>
  );
}

function SectionLabel({ children, noMargin }) {
  return (
    <Typography sx={{ fontFamily: 'GilroyBold', fontSize: 12, color: '#6b7368', letterSpacing: '0.07em', textTransform: 'uppercase', mb: noMargin ? 0 : 1.5 }}>
      {children}
    </Typography>
  );
}

function Field({ label, hint, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 13, color: '#1a1a1a', mb: 0.6 }}>{label}</Typography>
      {hint && <Typography sx={{ fontFamily: 'GilroyRegular', fontSize: 11.5, color: '#6b7368', mb: 0.6 }}>{hint}</Typography>}
      {children}
    </Box>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fcfcfa',
    borderRadius: '8px',
    fontFamily: 'GilroyRegular',
    fontSize: 13.5,
    '& fieldset': { borderColor: '#e6e8df' },
    '&:hover fieldset': { borderColor: '#9aa39a' },
    '&.Mui-focused fieldset': { borderColor: '#053418', borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'GilroyRegular', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#053418' },
};
